import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, PackagePlus, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { formatApiError } from "@/lib/api";
import { comboFingerprint, comboItemIssue, loadComboPreview } from "@/lib/combo";
import { ComboProducts } from "./ComboProducts";
import { ComboTotals } from "./ComboTotals";
import { cleanSizes, sizeSelectionError } from "@/lib/productDetails";

export const ComboPreview = ({ offerId, onClose }) => {
  const { items, addItem, setDrawerOpen } = useCart();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [choices, setChoices] = useState({});
  const setChoice = (id, value) => setChoices((previous) => ({ ...previous, [id]: { ...previous[id], ...value } }));
  const mounted = useRef(false);
  const addingRef = useRef(false);
  const addedRef = useRef(false);
  const openerRef = useRef(document.activeElement);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const data = await loadComboPreview(offerId);
      if (mounted.current) setPreview(data);
    } catch (err) {
      if (mounted.current) { setPreview(null); setError(formatApiError(err)); }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => { mounted.current = false; };
  }, [refresh]);

  const unavailable = preview && (!preview.quote || preview.members.some((member) => comboItemIssue(member, items)));
  const needsSizes = preview?.members.some(({ id, product, qty }) => product && sizeSelectionError(product, choices[id]?.size_quantities || {}, qty));
  const confirm = async () => {
    if (!preview || unavailable || needsSizes || addingRef.current) return;
    addingRef.current = true;
    setAdding(true);
    setNotice("");
    try {
      const fresh = await loadComboPreview(offerId);
      if (!mounted.current) return;
      setPreview(fresh);
      if (comboFingerprint(fresh) !== comboFingerprint(preview)) {
        setNotice("This combo has changed. Please review the updated products and total before adding it.");
        return;
      }
      if (!fresh.quote || fresh.members.some((member) => comboItemIssue(member, items))) return;
      if (fresh.members.some(({ id, product, qty }) => sizeSelectionError(product, choices[id]?.size_quantities || {}, qty))) {
        setNotice("Review the size quantities for every product in this combo.");
        return;
      }
      fresh.members.forEach(({ id, product, qty }) => addItem(product, qty, { silent: true, size_quantities: cleanSizes(choices[id]?.size_quantities || {}), details: choices[id]?.details }));
      addedRef.current = true;
      onClose();
      setDrawerOpen(true);
      toast.success(`${fresh.combo.title} added to your order`);
    } catch (err) {
      if (mounted.current) { setPreview(null); setError(formatApiError(err)); }
    } finally {
      addingRef.current = false;
      if (mounted.current) setAdding(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        data-testid="combo-preview-dialog"
        overlayProps={{ className: "z-[95]", "data-testid": "combo-preview-overlay" }}
        closeProps={{ "data-testid": "combo-preview-close", "aria-label": "Close combo preview", className: "flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white" }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (addedRef.current) return;
          const opener = openerRef.current?.isConnected ? openerRef.current : document.querySelector(`[data-testid="combo-view-${offerId}"]`);
          opener?.focus({ preventScroll: true });
        }}
        className="z-[100] flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-xl flex-col gap-0 overflow-hidden rounded-2xl border-[#F5C45A]/40 bg-[#12141A] p-0 text-slate-100"
      >
        <header className="max-h-[25dvh] shrink-0 overflow-y-auto border-b border-white/10 px-5 py-6 pr-16 sm:px-7 sm:pr-16" data-lenis-prevent>
          <span className="flex items-center gap-2 font-mono text-xs text-[#F5C45A]"><Sparkles size={14} aria-hidden="true" /> COMBO OFFER</span>
          <DialogTitle data-testid="combo-preview-title" className="mt-2 font-display text-base font-bold leading-snug [overflow-wrap:anywhere] md:text-lg">{preview?.combo.title || "Combo details"}</DialogTitle>
          <DialogDescription data-testid="combo-preview-description" className="mt-2 text-xs text-slate-400">{preview ? `${preview.members.reduce((sum, member) => sum + member.qty, 0)} pieces in this full set` : "Products and full-set pricing"}</DialogDescription>
        </header>
        <div data-testid="combo-preview-body" data-lenis-prevent className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 sm:px-7">
          {loading ? (
            <p data-testid="combo-preview-loading" role="status" className="flex items-center justify-center gap-3 py-16 text-sm text-slate-300"><Loader2 size={18} className="animate-spin" /> Checking combo details…</p>
          ) : error ? (
            <div className="py-10">
              <p data-testid="combo-preview-error" role="alert" className="text-sm text-rose-300 [overflow-wrap:anywhere]">{error}</p>
              <button data-testid="combo-preview-retry" onClick={refresh} className="mt-5 flex min-h-11 items-center gap-2 rounded-full border border-white/20 px-5 text-sm transition-colors hover:bg-white/10"><RefreshCw size={15} /> Try again</button>
            </div>
          ) : preview && (
            <>
              {notice && <p data-testid="combo-preview-change-notice" role="status" className="mt-5 text-sm text-[#F5C45A]">{notice}</p>}
              <ComboProducts members={preview.members} quote={preview.quote} cart={items} choices={choices} onChoice={setChoice} />
            </>
          )}
        </div>
        {!loading && !error && preview && (
          <footer className="shrink-0 border-t border-white/10 bg-[#0E1016] px-5 py-5 sm:px-7">
            {preview.quote && <ComboTotals quote={preview.quote} title={preview.combo.title} />}
            {unavailable && <p data-testid="combo-preview-unavailable" role="alert" className="mt-3 text-xs text-rose-300">This full combo is currently unavailable. No products have been added.</p>}
            {needsSizes && <p data-testid="combo-preview-sizes-required" role="status" className="mt-3 text-xs text-amber-300">Choose the required size quantities for each clothing product.</p>}
            <button
              data-testid={`combo-add-${offerId}`} onClick={confirm} disabled={adding || unavailable || needsSizes}
              className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-[#F5C45A] px-4 py-3 text-sm font-bold text-[#221407] transition-colors hover:bg-[#FFE9B0] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {adding ? <Loader2 size={18} className="animate-spin" /> : <PackagePlus size={18} />}
              {adding ? "Checking availability…" : "Add this combo to order"}
            </button>
          </footer>
        )}
      </DialogContent>
    </Dialog>
  );
};